(() => {
  function createAdapter({ api, catalog, request, storage, defaultSelected = true, shipmentLabel = 'This shipment', onChange = async () => {} }) {
    const ids = new Set(Object.values(catalog.variants).map(Number));
    const maximum = Math.max(...Object.keys(catalog.variants).map(Number));
    let queue = Promise.resolve();
    let choice;
    const owns = (item) => ids.has(Number(item.variant_id || item.id));
    const feeItems = (cart) => cart.items.filter(owns);
    const preferenceKey = (cart) => `ew-protection-choice-v1:${String(cart.token || '').split('?')[0]}`;
    const selectedFor = (cart) => {
      if (typeof choice === 'boolean') return choice;
      try {
        const value = storage?.getItem(preferenceKey(cart));
        if (value === 'true' || value === 'false') return value === 'true';
      } catch (_) {}
      return feeItems(cart).length > 0 || defaultSelected;
    };
    function quoteFor(cart, selected) {
      if (cart.currency !== 'USD') throw new Error('Protection requires USD');
      const cartDiscount = (cart.cart_level_discount_applications || []).reduce((sum, discount) => sum + discount.total_allocated_amount, 0);
      const items = cart.items.map((item) => {
        const handle = (item.handle || '').toLowerCase();
        const protection = owns(item) || /shipping-insurance|shipping-protection/.test(handle) || handle === 'order-protection';
        return { value: item.final_line_price, eligible: !item.gift_card && item.requires_shipping !== false && !protection && !handle.includes('warm-weather') };
      });
      const result = api.previewQuote({ currency: cart.currency, items, cartDiscount, strings: { shipment: shipmentLabel } }, { single: selected });
      return { currency: 'USD', quoteId: 'edelweiss-cart', revision: JSON.stringify(cart.items.map((item) => [item.key, item.quantity, item.final_line_price])), shipments: result.shipments };
    }
    function desiredItems(fee) {
      const items = [];
      if (fee >= maximum) {
        items.push({ id: catalog.variants[maximum], quantity: Math.floor(fee / maximum) });
        fee %= maximum;
      }
      if (fee) {
        const id = catalog.variants[fee];
        if (!id) throw new Error('Exact fee unavailable');
        items.push({ id, quantity: 1 });
      }
      return items;
    }
    async function reconcile(selection) {
      let cart = await request('cart.js');
      const selected = typeof selection === 'boolean' ? selection : selectedFor(cart);
      let changed = false;
      for (let attempt = 0; attempt < 4; attempt += 1) {
        const quote = quoteFor(cart, selected);
        const fee = quote.shipments.reduce((sum, item) => sum + (item.selected ? item.fee : 0), 0);
        const desired = desiredItems(fee);
        const current = feeItems(cart);
        const matches = current.length === desired.length && current.every((item) => desired.some((target) => Number(item.variant_id || item.id) === Number(target.id) && item.quantity === target.quantity && item.final_line_price === Number(Object.keys(catalog.variants).find((price) => Number(catalog.variants[price]) === Number(target.id))) * target.quantity));
        if (matches) {
          choice = selected;
          try { storage?.setItem(preferenceKey(cart), String(selected)); } catch (_) {}
          if (changed) await onChange(cart);
          return quote;
        }
        if (current.length) {
          await request('cart/update.js', { updates: Object.fromEntries(current.map((item) => [item.key, 0])) });
          changed = true;
        }
        if (desired.length) {
          await request('cart/add.js', { items: desired });
          changed = true;
        }
        cart = await request('cart.js');
      }
      throw new Error('Protection charge could not be confirmed');
    }
    const run = (selection) => {
      const next = queue.catch(() => {}).then(() => reconcile(selection));
      queue = next;
      return next;
    };
    return { getQuote: () => run(), setSelection: ({ selected }) => run(selected) };
  }
  if (typeof module !== 'undefined' && module.exports) { module.exports = { createAdapter }; return; }
  const script = document.currentScript;
  const root = window.Shopify?.routes?.root || '/';
  async function request(path, body) {
    if (body) await customElements.get('cart-note')?.flush?.();
    const response = await fetch(`${root}${path}`, {
      method: body ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store',
      headers: { Accept: 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}) },
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.description || error.message || `Cart update failed (${response.status})`);
    }
    return response.json();
  }
  async function refreshTotals(cart) {
    const footer = document.getElementById('main-cart-footer');
    document.querySelector('cart-items')?.classList.toggle('is-empty', cart.item_count === 0);
    const sectionIds = ['cart-icon-bubble', ...(footer?.dataset.id ? [footer.dataset.id] : [])];
    const rendered = await fetch(`${root}cart?sections=${encodeURIComponent(sectionIds.join(','))}`, { credentials: 'same-origin', cache: 'no-store' });
    if (!rendered.ok) throw new Error('Cart totals unavailable');
    const response = await rendered.json();
    for (const [id, html] of Object.entries(response)) {
      if (typeof html !== 'string') throw new Error(`Cart section unavailable: ${id}`);
      const doc = new DOMParser().parseFromString(html, 'text/html');
      if (id === 'cart-icon-bubble') {
        const target = document.getElementById('cart-icon-bubble');
        if (target) target.innerHTML = doc.querySelector('.shopify-section')?.innerHTML || html;
      } else {
        const source = doc.querySelector('#main-cart-footer .js-contents');
        const target = footer.querySelector('.js-contents');
        if (source && target) target.replaceWith(source);
        footer.classList.toggle('is-empty', cart.item_count === 0);
      }
    }
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') publish(PUB_SUB_EVENTS.cartUpdate, { source: 'ew-shipping-protection', cartData: cart });
  }
  fetch(script.dataset.variants, { credentials: 'same-origin' })
    .then((response) => { if (!response.ok) throw new Error('Protection configuration unavailable'); return response.json(); })
    .then((catalog) => window.EdelweissProtection.registerAdapter(createAdapter({
      api: window.EdelweissProtection, catalog, request,
      storage: { getItem: (key) => sessionStorage.getItem(key), setItem: (key, value) => sessionStorage.setItem(key, value) },
      defaultSelected: script.dataset.selected === 'true', shipmentLabel: script.dataset.shipment, onChange: refreshTotals,
    })))
    .catch((error) => { console.error('Edelweiss protection setup:', error.message); window.EdelweissProtection.failConnection?.(); });
})();
